// src/components/BCP/OperationsStep.tsx - Enhanced with detailed function management
import React, { useState } from 'react';
import { OperationsStepProps, CriticalFunction, KeySupplier, ITSystem } from '../../types/bcp';
import { BCPSuggestions, importCSVData, fileToBase64, getContextSpecificSuggestions } from '../../services/api';
import { ImportType } from '../../types/csvImport';
import InfoTooltip from './InfoTooltip';
import CSVImportModal from './CSVImportModal';
import ConsolidatedButton from './ConsolidatedButton';
import BulkImportModal from './BulkImportModal';

interface EnhancedOperationsStepProps extends OperationsStepProps {
  suggestions?: BCPSuggestions | null;
  onApplySuggestions?: () => void;
  bcpId?: number;
}

const OperationsStep: React.FC<EnhancedOperationsStepProps> = ({
  formData,
  setFormData,
  handleFieldChange,
  handleFieldBlur,
  shouldShowError,
  getFieldError,
  suggestions,
  onApplySuggestions,
  bcpId,
}) => {
  const [showFunctionModal, setShowFunctionModal] = useState(false);
  const [editingFunction, setEditingFunction] = useState<CriticalFunction | null>(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<KeySupplier | null>(null);
  const [showITModal, setShowITModal] = useState(false);
  const [editingIT, setEditingIT] = useState<ITSystem | null>(null);

  // CSV Import state
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [csvImportType] = useState<ImportType>(ImportType.FUNCTIONS);
  const [isImporting, setIsImporting] = useState(false);

  // Bulk Import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportType, setBulkImportType] = useState<ImportType>(ImportType.FUNCTIONS);

  // AI Generation state
  const [isGeneratingAIFunctions, setIsGeneratingAIFunctions] = useState(false);
  const [isGeneratingAISystems, setIsGeneratingAISystems] = useState(false);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // AI Generation Functions
  const handleGenerateAIFunctions = async () => {
    console.log('🤖 [AI FUNCTIONS] Button clicked!');
    console.log('🤖 [AI FUNCTIONS] bcpId:', bcpId);
    console.log('🤖 [AI FUNCTIONS] bcpId type:', typeof bcpId);

    if (!bcpId) {
      console.error('❌ [AI FUNCTIONS] BCP ID is required for AI function generation');
      console.error('❌ [AI FUNCTIONS] bcpId is:', bcpId);
      return;
    }

    setIsGeneratingAIFunctions(true);
    try {
      console.log('🤖 [AI FUNCTIONS] Starting context-specific AI function generation...');

      // Prepare current form data for context
      const contextData = {
        business_name: formData.business_name,
        business_type: formData.business_type,
        industry: formData.industry,
        employee_count: formData.employee_count,
        annual_revenue: formData.annual_revenue,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        existing_critical_functions: formData.critical_functions,
        existing_it_systems: formData.it_systems,
        existing_key_suppliers: formData.key_suppliers,
        risk_impact_assessments: formData.risk_impact_assessments
      };

      const response = await getContextSpecificSuggestions(bcpId, 'critical_functions', contextData);
      const aiSuggestions = response.suggestions;

      if (aiSuggestions.suggested_functions && aiSuggestions.suggested_functions.length > 0) {
        // Convert AI suggestions to CriticalFunction objects with full context
        const aiFunctions: CriticalFunction[] = aiSuggestions.suggested_functions.map((func: any) => ({
          id: generateId(),
          name: func.name || 'Unnamed Function',
          description: func.description || `AI-generated critical function: ${func.name}`,
          criticality: func.criticality || 'high',
          max_downtime: func.max_downtime || '24 hours',
          responsible_role: func.responsible_role || 'To be assigned',
          responsible_team: func.responsible_team || 'To be assigned',
          required_resources: func.required_resources || ['To be specified'],
          dependencies: func.dependencies || [],
          recovery_procedures: func.recovery_procedures || `Recovery procedures for ${func.name} will be defined during detailed planning.`,
          rto: func.rto || '24 hours',
          rpo: func.rpo || '4 hours',
          owner: func.owner || 'To be assigned',
          resources: func.resources || ['To be specified']
        }));

        // Add AI functions to existing functions
        const updatedFunctions = [...formData.critical_functions, ...aiFunctions];
        handleFieldChange('critical_functions', updatedFunctions);

        console.log(`✅ [AI FUNCTIONS] Generated ${aiFunctions.length} context-aware AI functions`);
      } else {
        console.log('⚠️ [AI FUNCTIONS] No AI suggestions available');
      }
    } catch (error) {
      console.error('❌ [AI FUNCTIONS] AI generation failed:', error);
    } finally {
      setIsGeneratingAIFunctions(false);
    }
  };

  const handleGenerateAISystems = async () => {
    console.log('🤖 [AI SYSTEMS] Button clicked!');
    console.log('🤖 [AI SYSTEMS] bcpId:', bcpId);
    console.log('🤖 [AI SYSTEMS] bcpId type:', typeof bcpId);

    if (!bcpId) {
      console.error('❌ [AI SYSTEMS] BCP ID is required for AI system generation');
      console.error('❌ [AI SYSTEMS] bcpId is:', bcpId);
      return;
    }

    setIsGeneratingAISystems(true);
    try {
      console.log('🤖 [AI SYSTEMS] Starting context-specific AI system generation...');

      // Prepare current form data for context
      const contextData = {
        business_name: formData.business_name,
        business_type: formData.business_type,
        industry: formData.industry,
        employee_count: formData.employee_count,
        annual_revenue: formData.annual_revenue,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        existing_critical_functions: formData.critical_functions,
        existing_it_systems: formData.it_systems,
        existing_key_suppliers: formData.key_suppliers,
        risk_impact_assessments: formData.risk_impact_assessments
      };

      const response = await getContextSpecificSuggestions(bcpId, 'it_systems', contextData);
      const aiSuggestions = response.suggestions;

      if (aiSuggestions.suggested_systems && aiSuggestions.suggested_systems.length > 0) {
        // Convert AI suggestions to ITSystem objects with full context
        const aiSystems: ITSystem[] = aiSuggestions.suggested_systems.map((system: any) => ({
          id: generateId(),
          name: system.name || 'Unnamed System',
          description: system.description || `AI-generated IT system: ${system.name}`,
          criticality: system.criticality || 'high',
          vendor: system.vendor || 'To be specified',
          backup_frequency: system.backup_frequency || 'Daily',
          recovery_time: system.recovery_time || '4 hours',
          dependencies: system.dependencies || []
        }));

        // Add AI systems to existing systems
        const updatedSystems = [...formData.it_systems, ...aiSystems];
        handleFieldChange('it_systems', updatedSystems);

        console.log(`✅ [AI SYSTEMS] Generated ${aiSystems.length} context-aware AI systems`);
      } else {
        console.log('⚠️ [AI SYSTEMS] No AI suggestions available');
      }
    } catch (error) {
      console.error('❌ [AI SYSTEMS] AI generation failed:', error);
    } finally {
      setIsGeneratingAISystems(false);
    }
  };


  // Critical Functions Management
  const addCriticalFunction = (functionData: Omit<CriticalFunction, 'id'>) => {
    const newFunction: CriticalFunction = {
      ...functionData,
      id: generateId(),
    };
    const updatedFunctions = [...formData.critical_functions, newFunction];
    handleFieldChange('critical_functions', updatedFunctions);
    setShowFunctionModal(false);
    setEditingFunction(null);
  };

  const updateCriticalFunction = (functionData: CriticalFunction) => {
    const updatedFunctions = formData.critical_functions.map(func =>
      func.id === functionData.id ? functionData : func
    );
    handleFieldChange('critical_functions', updatedFunctions);
    setShowFunctionModal(false);
    setEditingFunction(null);
  };

  const removeCriticalFunction = (id: string) => {
    const updatedFunctions = formData.critical_functions.filter(func => func.id !== id);
    handleFieldChange('critical_functions', updatedFunctions);
  };

  // Key Suppliers Management
  const addKeySupplier = (supplierData: Omit<KeySupplier, 'id'>) => {
    const newSupplier: KeySupplier = {
      ...supplierData,
      id: generateId(),
    };
    const updatedSuppliers = [...formData.key_suppliers, newSupplier];
    handleFieldChange('key_suppliers', updatedSuppliers);
    setShowSupplierModal(false);
    setEditingSupplier(null);
  };

  const updateKeySupplier = (supplierData: KeySupplier) => {
    const updatedSuppliers = formData.key_suppliers.map(supplier =>
      supplier.id === supplierData.id ? supplierData : supplier
    );
    handleFieldChange('key_suppliers', updatedSuppliers);
    setShowSupplierModal(false);
    setEditingSupplier(null);
  };

  const removeKeySupplier = (id: string) => {
    const updatedSuppliers = formData.key_suppliers.filter(supplier => supplier.id !== id);
    handleFieldChange('key_suppliers', updatedSuppliers);
  };

  // IT Systems Management
  const addITSystem = (systemData: Omit<ITSystem, 'id'>) => {
    const newSystem: ITSystem = {
      ...systemData,
      id: generateId(),
    };
    const updatedSystems = [...formData.it_systems, newSystem];
    handleFieldChange('it_systems', updatedSystems);
    setShowITModal(false);
    setEditingIT(null);
  };

  const updateITSystem = (systemData: ITSystem) => {
    const updatedSystems = formData.it_systems.map(system =>
      system.id === systemData.id ? systemData : system
    );
    handleFieldChange('it_systems', updatedSystems);
    setShowITModal(false);
    setEditingIT(null);
  };

  const removeITSystem = (id: string) => {
    const updatedSystems = formData.it_systems.filter(system => system.id !== id);
    handleFieldChange('it_systems', updatedSystems);
  };

  // CSV Import Functions
  const handleCSVImport = async (file: File) => {
    if (!bcpId) {
      alert('BCP ID is required for CSV import');
      return;
    }

    setIsImporting(true);

    try {
      const csvData = await fileToBase64(file);
      const result = await importCSVData(bcpId, {
        bcp_id: bcpId,
        import_type: csvImportType,
        csv_data: csvData
      });

      if (result.success) {
        // Refresh the form data by triggering a re-fetch or update
        // For now, we'll show a success message
        alert(`Successfully imported ${result.imported_count} ${csvImportType}`);
        setShowCSVImportModal(false);
      } else {
        // Show errors
        const errorMessage = result.errors.map(e => `Row ${e.row}: ${e.field} - ${e.error}`).join('\n');
        alert(`Import completed with errors:\n${errorMessage}`);
      }
    } catch (error) {
      console.error('CSV import error:', error);
      alert('Failed to import CSV file. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  // Remove unused functions - commented out to avoid ESLint warnings
  // const handleDownloadTemplate = async (importType: ImportType) => {
  //   try {
  //     const template = await getCSVTemplate(importType);
  //     downloadCSVTemplate(template.csv_content, template.filename);
  //   } catch (error) {
  //     console.error('Template download error:', error);
  //     alert('Failed to download template. Please try again.');
  //   }
  // };

  // const openCSVImportModal = (importType: ImportType) => {
  //   setCsvImportType(importType);
  //   setShowCSVImportModal(true);
  //   setImportResult(null);
  // };

  const openBulkImportModal = (importType: ImportType) => {
    setBulkImportType(importType);
    setShowBulkImportModal(true);
  };

  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          Critical Operations
          <InfoTooltip content="Are those business functions and supporting processes whose interruption would cause unacceptable harm to an organization's viability, customers, employees, reputation, or compliance obligations, and therefore require priority protection and recovery in the Business Continuity Plan (BCP)." />
        </h2>
        <p className="text-gray-600">Identify your most important business functions and resources with detailed recovery information</p>
      </div>

      {/* Critical Business Functions */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Critical Business Functions <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm text-gray-600">
              Define essential functions with detailed recovery requirements
            </p>
          </div>
          <div className="flex space-x-2">
            <ConsolidatedButton
              label="Add New"
              variant="blue"
              options={[
                {
                  label: 'Add Function',
                  value: 'add_function',
                  icon: '➕',
                  onClick: () => {
                    setEditingFunction(null);
                    setShowFunctionModal(true);
                  },
                },
                {
                  label: 'From AI',
                  value: 'from_ai',
                  icon: '🤖',
                  onClick: handleGenerateAIFunctions,
                  disabled: isGeneratingAIFunctions,
                  loading: isGeneratingAIFunctions
                },
                {
                  label: 'Bulk Import',
                  value: 'bulk_import',
                  icon: '📥',
                  onClick: () => openBulkImportModal(ImportType.FUNCTIONS),
                },
              ]}
            />
          </div>
        </div>

        <div className="space-y-3">
          {formData.critical_functions.map((func) => (
            <div key={func.id} className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-semibold text-gray-900">{func.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticalityColor(func.criticality)}`}>
                      {func.criticality.toUpperCase()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{func.description}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingFunction(func);
                      setShowFunctionModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => func.id && removeCriticalFunction(func.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                <div>
                  <span className="font-medium text-gray-700">Max Downtime:</span>
                  <p className="text-gray-600">{func.max_downtime}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Responsible:</span>
                  <p className="text-gray-600">{func.responsible_role} ({func.responsible_team})</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Resources:</span>
                  <p className="text-gray-600">{func.required_resources.join(', ')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-3 text-sm md:grid-cols-4">
                <div>
                  <span className="font-medium text-gray-700">RTO:</span>
                  <p className="text-gray-600">{func.rto || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">RPO:</span>
                  <p className="text-gray-600">{func.rpo || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Owner:</span>
                  <p className="text-gray-600">{func.owner || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Dependencies:</span>
                  <p className="text-gray-600">{(func.dependencies || []).length > 0 ? (func.dependencies || []).join(', ') : 'None'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {formData.critical_functions.length === 0 && (
          <div className="p-8 text-center rounded-lg border-2 border-gray-300 border-dashed">
            <svg className="mx-auto w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No critical functions defined</h3>
            <p className="mt-1 text-sm text-gray-500">Add your first critical business function to get started.</p>
          </div>
        )}
      </div>

      {/* Key Suppliers */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Key Suppliers</h3>
            <p className="text-sm text-gray-600">Identify critical suppliers and alternatives</p>
          </div>
          <div className="flex space-x-2">
            <ConsolidatedButton
              label="Add New"
              variant="green"
              options={[
                {
                  label: 'Add Supplier',
                  value: 'add_supplier',
                  icon: '➕',
                  onClick: () => {
                    setEditingSupplier(null);
                    setShowSupplierModal(true);
                  },
                },
                {
                  label: 'Bulk Import',
                  value: 'bulk_import',
                  icon: '📥',
                  onClick: () => openBulkImportModal(ImportType.SUPPLIERS),
                },
              ]}
            />
          </div>
        </div>

        <div className="space-y-3">
          {formData.key_suppliers.map((supplier) => (
            <div key={supplier.id} className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-semibold text-gray-900">{supplier.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticalityColor(supplier.criticality)}`}>
                      {supplier.criticality.toUpperCase()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{supplier.services_provided || 'No description'}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingSupplier(supplier);
                      setShowSupplierModal(true);
                    }}
                    className="text-green-600 hover:text-green-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeKeySupplier(supplier.id!)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div>
                  <span className="font-medium text-gray-700">Contact:</span>
                  <p className="text-gray-600">{supplier.contact_person || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Max Downtime:</span>
                  <p className="text-gray-600">{supplier.max_downtime || 'N/A'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* IT Systems */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">IT Systems</h3>
            <p className="text-sm text-gray-600">Identify critical IT infrastructure and systems</p>
          </div>
          <div className="flex space-x-2">
            <ConsolidatedButton
              label="Add New"
              variant="purple"
              options={[
                {
                  label: 'Add IT System',
                  value: 'add_it_system',
                  icon: '➕',
                  onClick: () => {
                    setEditingIT(null);
                    setShowITModal(true);
                  },
                },
                {
                  label: 'From AI',
                  value: 'from_ai',
                  icon: '🤖',
                  onClick: handleGenerateAISystems,
                  disabled: isGeneratingAISystems,
                  loading: isGeneratingAISystems
                },
                {
                  label: 'Bulk Import',
                  value: 'bulk_import',
                  icon: '📥',
                  onClick: () => openBulkImportModal(ImportType.IT_SYSTEMS),
                },
              ]}
            />
          </div>
        </div>

        <div className="space-y-3">
          {formData.it_systems.map((system) => (
            <div key={system.id} className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-semibold text-gray-900">{system.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticalityColor(system.criticality)}`}>
                      {system.criticality.toUpperCase()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{system.description || 'No description'}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingIT(system);
                      setShowITModal(true);
                    }}
                    className="text-purple-600 hover:text-purple-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeITSystem(system.id!)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <p className="text-gray-600">{system.system_type || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">RTO:</span>
                  <p className="text-gray-600">{system.rto || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">RPO:</span>
                  <p className="text-gray-600">{system.rpo || 'N/A'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{formData.critical_functions.length}</div>
            <div className="text-sm text-blue-800">Critical Functions</div>
          </div>
        </div>
        <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{formData.key_suppliers.length}</div>
            <div className="text-sm text-green-800">Key Suppliers</div>
          </div>
        </div>
        <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{formData.it_systems.length}</div>
            <div className="text-sm text-purple-800">IT Systems</div>
          </div>
        </div>
      </div>

      {/* Function Modal */}
      {showFunctionModal && (
        <FunctionModal
          function={editingFunction}
          onSave={(func) => {
            if (editingFunction) {
              updateCriticalFunction(func as CriticalFunction);
            } else {
              addCriticalFunction(func as Omit<CriticalFunction, 'id'>);
            }
          }}
          onClose={() => {
            setShowFunctionModal(false);
            setEditingFunction(null);
          }}
        />
      )}

      {/* Supplier Modal */}
      {showSupplierModal && (
        <SupplierModal
          supplier={editingSupplier}
          onSave={(supplier) => {
            if (editingSupplier) {
              updateKeySupplier(supplier as KeySupplier);
            } else {
              addKeySupplier(supplier as Omit<KeySupplier, 'id'>);
            }
          }}
          onClose={() => {
            setShowSupplierModal(false);
            setEditingSupplier(null);
          }}
        />
      )}

      {/* IT System Modal */}
      {showITModal && (
        <ITSystemModal
          system={editingIT}
          onSave={(system) => {
            if (editingIT) {
              updateITSystem(system as ITSystem);
            } else {
              addITSystem(system as Omit<ITSystem, 'id'>);
            }
          }}
          onClose={() => {
            setShowITModal(false);
            setEditingIT(null);
          }}
        />
      )}

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showCSVImportModal}
        onClose={() => setShowCSVImportModal(false)}
        importType={csvImportType}
        onImport={handleCSVImport}
        isImporting={isImporting}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        importType={bulkImportType}
        onImport={handleCSVImport}
        isImporting={isImporting}
      />
    </div>
  );
};

// Function Modal Component
interface FunctionModalProps {
  function: CriticalFunction | null;
  onSave: (func: CriticalFunction | Omit<CriticalFunction, 'id'>) => void;
  onClose: () => void;
}

const FunctionModal: React.FC<FunctionModalProps> = ({ function: editFunction, onSave, onClose }) => {
  const [formData, setFormData] = useState<Omit<CriticalFunction, 'id'>>({
    name: editFunction?.name || '',
    description: editFunction?.description || '',
    criticality: editFunction?.criticality || 'high',
    max_downtime: editFunction?.max_downtime || '',
    responsible_role: editFunction?.responsible_role || '',
    responsible_team: editFunction?.responsible_team || '',
    required_resources: editFunction?.required_resources || [],
    dependencies: editFunction?.dependencies || [],
    recovery_procedures: editFunction?.recovery_procedures || '',
    rto: editFunction?.rto || '',
    rpo: editFunction?.rpo || '',
    owner: editFunction?.owner || '',
    resources: editFunction?.resources || [],
  });

  const [newResource, setNewResource] = useState('');
  const [newDependency, setNewDependency] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editFunction) {
      onSave({ ...formData, id: editFunction.id });
    } else {
      onSave(formData);
    }
  };

  const addResource = () => {
    if (newResource.trim()) {
      setFormData(prev => ({
        ...prev,
        required_resources: [...prev.required_resources, newResource.trim()]
      }));
      setNewResource('');
    }
  };

  const removeResource = (index: number) => {
    setFormData(prev => ({
      ...prev,
      required_resources: prev.required_resources.filter((_, i) => i !== index)
    }));
  };

  const addDependency = () => {
    if (newDependency.trim()) {
      setFormData(prev => ({
        ...prev,
        dependencies: [...prev.dependencies, newDependency.trim()]
      }));
      setNewDependency('');
    }
  };

  const removeDependency = (index: number) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="overflow-y-auto fixed inset-0 z-50">
      <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block overflow-hidden p-6 my-8 w-full max-w-2xl text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {editFunction ? 'Edit Critical Function' : 'Add Critical Function'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Function Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Customer Service Operations"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Criticality Level <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.criticality}
                  onChange={(e) => setFormData(prev => ({ ...prev, criticality: e.target.value as CriticalFunction['criticality'] }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe what this function does and why it's critical..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Maximum Tolerable Downtime <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.max_downtime}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_downtime: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select downtime tolerance</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Responsible Role <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.responsible_role}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsible_role: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Operations Manager"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Responsible Team <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.responsible_team}
                onChange={(e) => setFormData(prev => ({ ...prev, responsible_team: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Customer Support Team"
              />
            </div>

            {/* Required Resources */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Required Resources</label>
              <div className="space-y-2">
                {formData.required_resources.map((resource, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm">{resource}</span>
                    <button
                      type="button"
                      onClick={() => removeResource(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newResource}
                    onChange={(e) => setNewResource(e.target.value)}
                    placeholder="Add required resource..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResource())}
                  />
                  <button
                    type="button"
                    onClick={addResource}
                    className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Dependencies */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Dependencies</label>
              <div className="space-y-2">
                {formData.dependencies.map((dependency, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm">{dependency}</span>
                    <button
                      type="button"
                      onClick={() => removeDependency(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newDependency}
                    onChange={(e) => setNewDependency(e.target.value)}
                    placeholder="Add dependency..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDependency())}
                  />
                  <button
                    type="button"
                    onClick={addDependency}
                    className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* RTO and RPO */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Recovery Time Objective (RTO) <span className="text-red-500">*</span>
                  <InfoTooltip content="RTO is the maximum acceptable time to restore a business function after a disruption. It determines how quickly you need to get back up and running to avoid significant business impact." />
                </label>
                <select
                  value={formData.rto}
                  onChange={(e) => setFormData(prev => ({ ...prev, rto: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select RTO</option>
                  <option value="ai-decide">🤖 Let AI Decide</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Recovery Point Objective (RPO) <span className="text-red-500">*</span>
                  <InfoTooltip content="RPO is the maximum acceptable amount of data loss measured in time. It determines how often you need to backup your data to avoid losing critical information during a disruption." />
                </label>
                <select
                  value={formData.rpo}
                  onChange={(e) => setFormData(prev => ({ ...prev, rpo: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select RPO</option>
                  <option value="ai-decide">🤖 Let AI Decide</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>
            </div>

            {/* Owner */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Function Owner <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.owner}
                onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., John Doe, Operations Director"
              />
            </div>

            {/* Resources */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Additional Resources</label>
              <div className="space-y-2">
                {formData.resources.map((resource, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm">{resource}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          resources: prev.resources.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newResource}
                    onChange={(e) => setNewResource(e.target.value)}
                    placeholder="Add additional resource..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResource())}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newResource.trim()) {
                        setFormData(prev => ({
                          ...prev,
                          resources: [...prev.resources, newResource.trim()]
                        }));
                        setNewResource('');
                      }
                    }}
                    className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Recovery Procedures */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Recovery Procedures</label>
              <textarea
                rows={4}
                value={formData.recovery_procedures}
                onChange={(e) => setFormData(prev => ({ ...prev, recovery_procedures: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the step-by-step recovery procedures for this function..."
              />
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                {editFunction ? 'Update Function' : 'Add Function'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Supplier Modal Component
interface SupplierModalProps {
  supplier: KeySupplier | null;
  onSave: (supplier: KeySupplier | Omit<KeySupplier, 'id'>) => void;
  onClose: () => void;
}

const SupplierModal: React.FC<SupplierModalProps> = ({ supplier: editSupplier, onSave, onClose }) => {
  const [formData, setFormData] = useState<Omit<KeySupplier, 'id'>>({
    name: editSupplier?.name || '',
    contact_person: editSupplier?.contact_person || '',
    phone: editSupplier?.phone || '',
    email: editSupplier?.email || '',
    services_provided: editSupplier?.services_provided || '',
    criticality: editSupplier?.criticality || 'medium',
    alternative_suppliers: editSupplier?.alternative_suppliers || [],
    max_downtime: editSupplier?.max_downtime || '',
    recovery_priority: editSupplier?.recovery_priority || '',
  });

  const [newAlternative, setNewAlternative] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editSupplier) {
      onSave({ ...formData, id: editSupplier.id });
    } else {
      onSave(formData);
    }
  };

  const addAlternative = () => {
    if (newAlternative.trim()) {
      setFormData(prev => ({
        ...prev,
        alternative_suppliers: [...prev.alternative_suppliers, newAlternative.trim()]
      }));
      setNewAlternative('');
    }
  };

  const removeAlternative = (index: number) => {
    setFormData(prev => ({
      ...prev,
      alternative_suppliers: prev.alternative_suppliers.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="overflow-y-auto fixed inset-0 z-50">
      <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block overflow-hidden p-6 my-8 w-full max-w-lg text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {editSupplier ? 'Edit Key Supplier' : 'Add Key Supplier'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Supplier Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., ABC Logistics"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Criticality Level <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.criticality}
                  onChange={(e) => setFormData(prev => ({ ...prev, criticality: e.target.value as KeySupplier['criticality'] }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., John Smith"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="contact@supplier.com"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Services Provided</label>
              <textarea
                rows={3}
                value={formData.services_provided || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, services_provided: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Describe the services this supplier provides..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Max Downtime</label>
                <select
                  value={formData.max_downtime || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_downtime: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select downtime tolerance</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Recovery Priority</label>
                <select
                  value={formData.recovery_priority || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, recovery_priority: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select priority</option>
                  <option value="immediate">Immediate</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {/* Alternative Suppliers */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Alternative Suppliers</label>
              <div className="space-y-2">
                {formData.alternative_suppliers.map((supplier, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm">{supplier}</span>
                    <button
                      type="button"
                      onClick={() => removeAlternative(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newAlternative}
                    onChange={(e) => setNewAlternative(e.target.value)}
                    placeholder="Add alternative supplier..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAlternative())}
                  />
                  <button
                    type="button"
                    onClick={addAlternative}
                    className="px-3 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                {editSupplier ? 'Update Supplier' : 'Add Supplier'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// IT System Modal Component
interface ITSystemModalProps {
  system: ITSystem | null;
  onSave: (system: ITSystem | Omit<ITSystem, 'id'>) => void;
  onClose: () => void;
}

const ITSystemModal: React.FC<ITSystemModalProps> = ({ system: editSystem, onSave, onClose }) => {
  const [formData, setFormData] = useState<Omit<ITSystem, 'id'>>({
    name: editSystem?.name || '',
    description: editSystem?.description || '',
    criticality: editSystem?.criticality || 'medium',
    max_downtime: editSystem?.max_downtime || '',
    responsible_team: editSystem?.responsible_team || '',
    backup_location: editSystem?.backup_location || '',
    recovery_procedures: editSystem?.recovery_procedures || '',
    rto: editSystem?.rto || '',
    rpo: editSystem?.rpo || '',
    system_type: editSystem?.system_type || 'other',
    vendor: editSystem?.vendor || '',
    supports_functions: editSystem?.supports_functions || [],
    system_dependencies: editSystem?.system_dependencies || [],
    recovery_priority: editSystem?.recovery_priority || '',
  });

  const [newSupportFunction, setNewSupportFunction] = useState('');
  const [newDependency, setNewDependency] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editSystem) {
      onSave({ ...formData, id: editSystem.id });
    } else {
      onSave(formData);
    }
  };

  const addSupportFunction = () => {
    if (newSupportFunction.trim()) {
      setFormData(prev => ({
        ...prev,
        supports_functions: [...(prev.supports_functions || []), newSupportFunction.trim()]
      }));
      setNewSupportFunction('');
    }
  };

  const removeSupportFunction = (index: number) => {
    setFormData(prev => ({
      ...prev,
      supports_functions: (prev.supports_functions || []).filter((_, i) => i !== index)
    }));
  };

  const addSystemDependency = () => {
    if (newDependency.trim()) {
      setFormData(prev => ({
        ...prev,
        system_dependencies: [...(prev.system_dependencies || []), newDependency.trim()]
      }));
      setNewDependency('');
    }
  };

  const removeSystemDependency = (index: number) => {
    setFormData(prev => ({
      ...prev,
      system_dependencies: (prev.system_dependencies || []).filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="overflow-y-auto fixed inset-0 z-50">
      <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block overflow-hidden p-6 my-8 w-full max-w-lg text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {editSystem ? 'Edit IT System' : 'Add IT System'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  System Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Customer Database"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Criticality Level <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.criticality}
                  onChange={(e) => setFormData(prev => ({ ...prev, criticality: e.target.value as ITSystem['criticality'] }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Description</label>
              <textarea
                rows={3}
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Describe what this system does..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">System Type</label>
                <select
                  value={formData.system_type || 'other'}
                  onChange={(e) => setFormData(prev => ({ ...prev, system_type: e.target.value as ITSystem['system_type'] }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="database">Database</option>
                  <option value="application">Application</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="network">Network</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Vendor</label>
                <input
                  type="text"
                  value={formData.vendor || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Microsoft, Oracle"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Max Downtime</label>
                <select
                  value={formData.max_downtime || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_downtime: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select downtime tolerance</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Recovery Priority</label>
                <select
                  value={formData.recovery_priority || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, recovery_priority: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select priority</option>
                  <option value="immediate">Immediate</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  RTO
                  <InfoTooltip content="RTO is the maximum acceptable time to restore a business function after a disruption. It determines how quickly you need to get back up and running to avoid significant business impact." />
                </label>
                <select
                  value={formData.rto || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, rto: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select RTO</option>
                  <option value="ai-decide">🤖 Let AI Decide</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  RPO
                  <InfoTooltip content="RPO is the maximum acceptable amount of data loss measured in time. It determines how often you need to backup your data to avoid losing critical information during a disruption." />
                </label>
                <select
                  value={formData.rpo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, rpo: e.target.value }))}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select RPO</option>
                  <option value="ai-decide">🤖 Let AI Decide</option>
                  <option value="0-1 hour">0-1 hour</option>
                  <option value="1-4 hours">1-4 hours</option>
                  <option value="4-24 hours">4-24 hours</option>
                  <option value="1-3 days">1-3 days</option>
                  <option value="3-7 days">3-7 days</option>
                  <option value="More than 1 week">More than 1 week</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Responsible Team</label>
              <input
                type="text"
                value={formData.responsible_team || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, responsible_team: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., IT Operations Team"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Backup Location</label>
              <input
                type="text"
                value={formData.backup_location || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, backup_location: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Cloud backup, Secondary data center"
              />
            </div>

            {/* Supported Functions */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Supported Business Functions</label>
              <div className="space-y-2">
                {(formData.supports_functions || []).map((func, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm">{func}</span>
                    <button
                      type="button"
                      onClick={() => removeSupportFunction(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newSupportFunction}
                    onChange={(e) => setNewSupportFunction(e.target.value)}
                    placeholder="Add supported function..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSupportFunction())}
                  />
                  <button
                    type="button"
                    onClick={addSupportFunction}
                    className="px-3 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* System Dependencies */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">System Dependencies</label>
              <div className="space-y-2">
                {(formData.system_dependencies || []).map((dependency, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm">{dependency}</span>
                    <button
                      type="button"
                      onClick={() => removeSystemDependency(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newDependency}
                    onChange={(e) => setNewDependency(e.target.value)}
                    placeholder="Add system dependency..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSystemDependency())}
                    />
                  <button
                    type="button"
                    onClick={addSystemDependency}
                    className="px-3 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Recovery Procedures</label>
              <textarea
                rows={4}
                value={formData.recovery_procedures || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, recovery_procedures: e.target.value }))}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Describe the step-by-step recovery procedures for this system..."
              />
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
              >
                {editSystem ? 'Update System' : 'Add System'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OperationsStep;